import { useTranslation } from "react-i18next";
import { useCallback } from "react";
import { FirestoreGenerationStatus } from "reedy-data/api";
import { toast } from "utils/toast";
import { logError } from "shared/services/ErrorReporting";
import { useApi } from "hooks/useApi";
import { downloadFile } from "utils/helpers";
import { formatDateTime } from "shared/utils";
import { COMPLETED_STATUSES } from "shared/constants";
import startCase from "lodash/startCase";
import { ActionCard } from "components/Card/ActionCard";

interface Evaluation {
  id?: string;
  status?: FirestoreGenerationStatus;
  showStatus?: boolean;
  numberOfBooks: number;
  createdAt?: Date;
}

export const EvaluationCard = ({
  id,
  status,
  showStatus,
  numberOfBooks,
  createdAt,
}: Evaluation) => {
  const { t } = useTranslation(["books"]);
  const api = useApi();

  const handleDownload = useCallback(async () => {
    try {
      if (!id) {
        throw new Error("Evaluation ID not found.");
      }

      // Get the latest evaluation data to ensure we have the most up-to-date file URL
      const response = await api.evaluation.getEvaluation(id);
      if (!response) {
        throw new Error("Download failed. Please try again.");
      }
      const fileUrl = response.file?.url as string;
      const fileName = (response.file?.name as string) || "evaluation.xlsx";

      if (!fileUrl) {
        throw new Error("File URL not found.");
      }
      downloadFile(fileUrl, fileName);
    } catch (error) {
      logError(`Download failed: ${error}`);
      toast.error(t("books:evaluation.downloadError"));
    }
  }, [api.evaluation, id, t]);

  const createdAtStr = createdAt
    ? formatDateTime(createdAt)
    : t("books:evaluation.card.unknownCreatedAt");

  const subtitleParts = [
    `${t("books:evaluation.card.numberOfBooks")}: ${numberOfBooks}`,
  ];

  if (status && showStatus) {
    subtitleParts.push(
      `${t("books:evaluation.card.status")}: ${startCase(status.toLowerCase())}`,
    );
  }

  const subtitle = subtitleParts.join(" • ");

  return (
    <ActionCard
      title={createdAtStr}
      subtitle={subtitle}
      iconName={
        status && COMPLETED_STATUSES.includes(status) ? "chart-area" : "loader"
      }
    >
      {status === FirestoreGenerationStatus.COMPLETED && (
        <ActionCard.Action
          iconName="download"
          label={t("books:evaluation.actions.download")}
          variant="tertiary"
          onClick={handleDownload}
          testId="evaluation-card-download"
        />
      )}
    </ActionCard>
  );
};
